import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * WorkflowTemplatesPage
 *
 * Template gallery for workflow templates. Supports browsing, creating from template,
 * saving workflows as templates, and deleting templates.
 *
 * Requirements: 10.1, 10.2, 10.3, 10.4, 10.5
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { workflowBuilderApi } from '@/api/workflowBuilderApi';
const CATEGORY_ICONS = {
    analysis: '🔬',
    processing: '⚙️',
    notification: '📢',
    integration: '🔗',
    default: '📋',
};
const WorkflowTemplatesPage = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [instantiating, setInstantiating] = useState(null);
    const loadTemplates = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await workflowBuilderApi.listTemplates();
            setTemplates(data);
        }
        catch (err) {
            setError(err.response?.data?.message || 'Failed to load templates');
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        void loadTemplates();
    }, [loadTemplates]);
    const handleInstantiate = useCallback(async (templateId) => {
        try {
            setInstantiating(templateId);
            setError(null);
            const workflow = await workflowBuilderApi.instantiateTemplate(templateId);
            navigate(`/workflows/builder/${workflow.id}`);
        }
        catch (err) {
            setError(err.response?.data?.message || 'Failed to create workflow from template');
        }
        finally {
            setInstantiating(null);
        }
    }, [navigate]);
    const handleDelete = useCallback(async () => {
        if (!deleteTarget)
            return;
        try {
            setError(null);
            // Templates use the same delete pattern — call API then refresh
            await workflowBuilderApi.deleteWorkflow(deleteTarget.id);
            setDeleteTarget(null);
            await loadTemplates();
        }
        catch (err) {
            setError(err.response?.data?.message || 'Failed to delete template');
        }
    }, [deleteTarget, loadTemplates]);
    const categories = ['all', ...new Set(templates.map((t) => t.category).filter(Boolean))];
    const filtered = templates.filter((t) => {
        const matchesSearch = !searchQuery ||
            t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || t.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });
    if (loading) {
        return (_jsx("div", { className: "flex items-center justify-center h-64", children: _jsx(LoadingSpinner, { size: "lg" }) }));
    }
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: "Workflow Templates", subtitle: "Browse and create workflows from pre-built templates", breadcrumbs: [
                    { label: 'Agents', href: '/agents' },
                    { label: 'Workflow Templates' },
                ], action: _jsx("button", { onClick: () => navigate('/workflows/builder'), className: "btn btn-primary", children: "New Blank Workflow" }) }), error && _jsx(ErrorAlert, { message: error, onDismiss: () => setError(null) }), _jsxs("div", { className: "flex flex-wrap items-center gap-4 mb-6", children: [_jsx("input", { type: "text", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), placeholder: "Search templates...", className: "border border-gray-300 rounded-md px-3 py-2 text-sm w-64 focus:ring-2 focus:ring-purple-500 focus:border-transparent", "aria-label": "Search templates" }), _jsx("div", { className: "flex gap-2", children: categories.map((cat) => (_jsx("button", { onClick: () => setSelectedCategory(cat), className: `px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${selectedCategory === cat
                                ? 'bg-purple-600 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`, children: cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1) }, cat))) })] }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6", children: filtered.map((template) => (_jsxs("div", { className: "card hover:shadow-md transition-shadow", children: [_jsx("div", { className: "flex items-start justify-between mb-3", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-2xl", children: CATEGORY_ICONS[template.category] || CATEGORY_ICONS.default }), _jsxs("div", { children: [_jsx("h3", { className: "text-base font-semibold text-gray-900", children: template.name }), _jsx("span", { className: "text-xs text-gray-500", children: template.category })] })] }) }), _jsx("p", { className: "text-sm text-gray-600 mb-3 line-clamp-2", children: template.description }), template.metadata?.useCase && (_jsxs("p", { className: "text-xs text-gray-500 mb-3", children: ["Use case: ", template.metadata.useCase] })), _jsxs("div", { className: "flex items-center justify-between text-xs text-gray-400 mb-4", children: [_jsxs("span", { children: [template.definition?.steps?.length ?? 0, " steps"] }), _jsx("span", { children: new Date(template.createdAt).toLocaleDateString() })] }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { onClick: () => handleInstantiate(template.id), disabled: instantiating === template.id, className: "btn btn-primary flex-1 text-sm", children: instantiating === template.id ? 'Creating...' : 'Use Template' }), _jsx("button", { onClick: () => setDeleteTarget(template), className: "btn btn-secondary text-sm px-3", "aria-label": `Delete template ${template.name}`, children: "\uD83D\uDDD1\uFE0F" })] })] }, template.id))) }), filtered.length === 0 && !loading && (_jsx("div", { className: "text-center py-12", children: _jsx("p", { className: "text-gray-500", children: searchQuery || selectedCategory !== 'all'
                        ? 'No templates match your filters'
                        : 'No templates available yet' }) })), _jsx(ConfirmDialog, { isOpen: deleteTarget !== null, title: "Delete Template", message: `Delete "${deleteTarget?.name}"? This will not affect workflows already created from this template.`, confirmLabel: "Delete", cancelLabel: "Cancel", variant: "danger", onConfirm: handleDelete, onCancel: () => setDeleteTarget(null) })] }));
};
export default WorkflowTemplatesPage;
